import {
  DEFAULT_PAGE,
  DEFAULT_ROWS_PER_PAGE,
  PATH_TEACHER_REQUEST_MANAGEMENT,
} from '@configs'
import { Skeleton, Tooltip } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SharedTable } from 'src/common'

import { FormProvider, useForm } from 'react-hook-form'
import {
  RootState,
  getListTeacherRequest,
  updateFilterValue,
  useAppDispatch,
} from '@redux'
import { useSelector } from 'react-redux'
import { useDebounce } from 'src/hooks/useDebounce'
import moment from 'moment'
import styled from 'styled-components'
import {
  SEARCH_FORM_FIELD_DEFAULT_VALUES,
  SEARCH_FORM_FIELDS,
  TABLE_COLUMNS,
} from 'src/pages/teacher-request-management/config'
import TeacherRequestTableToolbar from './TeacherRequestTableToolbar'
import MiniDashboard from './MiniDashboard'
import { EyeOutlined } from '@ant-design/icons'
import { ITeacherRequestRowData } from 'src/interfaces/teacher-request-management'
import { useNavigate } from 'react-router-dom'
import useDeepCompareEffect from 'use-deep-compare-effect'

export default function ListTeacherRequest() {
  const { t } = useTranslation(['teacherRequest'])
  const navigate = useNavigate()

  const dispatch = useAppDispatch()
  const { data: listTeacherRequestData, isLoading } = useSelector(
    (state: RootState) => state.teacherRequestManagement.listTeacherRequest
  )
  const teacherRequestFilterValue = useSelector(
    (state: RootState) =>
      state.teacherRequestManagement.teacherRequestFilterValue
  )

  const [page, setPage] = useState(DEFAULT_PAGE)

  const methods = useForm({
    defaultValues: {
      ...SEARCH_FORM_FIELD_DEFAULT_VALUES,
      [SEARCH_FORM_FIELDS.SEARCH]: teacherRequestFilterValue?.search || '',
      [SEARCH_FORM_FIELDS.DATE_RANGE]:
        teacherRequestFilterValue?.dateRange || [],
      [SEARCH_FORM_FIELDS.REQUEST_STATUS]:
        teacherRequestFilterValue?.requestStatus || [],
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })
  const { watch } = methods

  const searchDebounceValue = useDebounce(watch(SEARCH_FORM_FIELDS.SEARCH))
  const requestStatusValue = watch(SEARCH_FORM_FIELDS.REQUEST_STATUS)
  const dateRangeValue = watch(SEARCH_FORM_FIELDS.DATE_RANGE)

  const dateRangeParams = useMemo(() => {
    if (!Array.isArray(dateRangeValue) || !dateRangeValue?.length) return

    const deadlineFrom = moment(dateRangeValue?.[0]).startOf('day')
    const deadlineTo = moment(dateRangeValue?.[1]).endOf('day')

    return {
      // From 00:00:00 of start day
      startDate: deadlineFrom.toISOString(),
      // To 23:59:59 of end day
      endDate: deadlineTo.toISOString(),
    }
  }, [dateRangeValue])

  const queryParams = useMemo(() => {
    return {
      ...(!!searchDebounceValue && {
        search: String(searchDebounceValue).trim(),
      }),
      ...(Array.isArray(requestStatusValue) &&
        !!requestStatusValue.length && {
          statusRequest: requestStatusValue.join(','),
        }),
      ...(dateRangeParams?.startDate && {
        startDate: dateRangeParams?.startDate,
      }),
      ...(dateRangeParams?.endDate &&
        ({
          endDate: dateRangeParams?.endDate,
        } as any)),
    }
  }, [
    searchDebounceValue,
    requestStatusValue,
    dateRangeParams?.startDate,
    dateRangeParams?.endDate,
  ])

  const columns = useMemo(() => {
    return Object.values(TABLE_COLUMNS)
      ?.map((column) => {
        return {
          ...column,
          ellipsis: true,
          title: column.title ? t(String(column.title) as any) : '',
          ...(TABLE_COLUMNS.TEACHER_REQUEST_ACTION.key === column.key && {
            render: (_: any, record: ITeacherRequestRowData) => {
              return (
                <div className="flex items-center justify-center h-full w-full gap-2">
                  <Tooltip title="View">
                    <EyeOutlined
                      onClick={() =>
                        navigate(
                          `${PATH_TEACHER_REQUEST_MANAGEMENT}/${record?.id}`
                        )
                      }
                    />
                  </Tooltip>
                </div>
              )
            },
          }),
        }
      })
      .filter(Boolean)
  }, [navigate, t])

  const listTeacherRequestFmt = useMemo(() => {
    if (
      !Array.isArray(listTeacherRequestData?.items) ||
      !listTeacherRequestData?.items.length
    )
      return []

    return listTeacherRequestData.items.map((it: any, idx: number) => {
      const {
        id,
        personalInformation,
        createdAt: requestedDate,
        updatedAt,
        responseData,
        status,
      } = it || {}
      return {
        index: idx + 1,
        id,
        requesterName: personalInformation?.fullName,
        requestedDate,
        updatedAt,
        responseData,
        status,
      }
    })
  }, [listTeacherRequestData.items])

  useDeepCompareEffect(() => {
    dispatch(
      getListTeacherRequest({
        page,
        limit: DEFAULT_ROWS_PER_PAGE,
        ...queryParams,
      })
    )
  }, [dispatch, page, queryParams])

  useEffect(() => {
    setPage(DEFAULT_PAGE)
  }, [
    requestStatusValue,
    dateRangeParams?.startDate,
    dateRangeParams?.endDate,
    searchDebounceValue,
  ])

  useEffect(() => {
    return () => {
      dispatch(
        updateFilterValue({
          requestStatus: requestStatusValue,
          dateRange: dateRangeValue,
          search: searchDebounceValue,
        })
      )
    }
  }, [dateRangeValue, dispatch, requestStatusValue, searchDebounceValue])

  return (
    <>
      <div className="bg-white rounded-md p-4">
        <FormProvider {...methods}>
          <div className="w-full">
            <MiniDashboard />

            <form className="mt-10">
              <TeacherRequestTableToolbar />
            </form>
          </div>
        </FormProvider>

        {isLoading ? (
          <Skeleton
            paragraph={{ rows: 10 }}
            style={{
              marginTop: 10,
            }}
          />
        ) : (
          <SharedTableStyled>
            <SharedTable
              columns={columns as any}
              dataSource={listTeacherRequestFmt}
              totalResults={Number(listTeacherRequestData?.total)}
              paginationProps={{
                total: !!listTeacherRequestData?.total
                  ? Number(listTeacherRequestData?.total)
                  : undefined,
                pageSize: 10,
                showSizeChanger: false,
                current: page,
                onChange(page) {
                  setPage(page)
                },
              }}
              tableProps={{
                scroll: {
                  y: 600,
                },
              }}
            />
          </SharedTableStyled>
        )}
      </div>
    </>
  )
}

const SharedTableStyled = styled.div`
  .ant-table-wrapper {
    margin: 0 !important;
    margin-top: 16px !important;
    max-height: unset;
  }
`
